// apps/cli/src/commands/enterprise/campaigns/create.ts
import type { Command } from 'commander';
import { randomUUID } from 'node:crypto';
import { unwrapDataResponse } from '@wport/core';
import { enterprisePost } from '../../../lib/enterprise-client';
import { resolveApiKey } from '../../../lib/credentials-store';
import { resolveContext } from '../../../lib/global-opts';
import { readJsonObject } from '../../../lib/io-helpers';
import { printJson } from '../../../lib/output';
import type { WriteCtx } from '../jobs/write-shared';

interface CreateFlags {
	file?: string;
	idempotencyKey?: string;
}

/** create/update 回應：後端回含 { enc_id } 的 campaign VM。 */
export interface CreatedCampaign {
	enc_id?: string;
	[k: string]: unknown;
}

/**
 * `campaigns create` 核心：讀 --file/stdin JSON → POST /campaigns（201）→ 回含 enc_id 的結果。
 * body 直送後端 CreateCampaignDto 驗證；enc_job_ids 由後端 controller 解密（CLI 不碰）。
 */
export async function runCampaignCreate(
	ctx: WriteCtx,
	apiKey: string,
	source: string,
	idempotencyKey: string
): Promise<void> {
	const campaignBody = readJsonObject(source, { timeoutMs: ctx.timeoutMs });
	const { body } = await enterprisePost(
		{ baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey },
		'/campaigns',
		campaignBody,
		{ idempotencyKey }
	);
	const created = unwrapDataResponse<CreatedCampaign>(body);
	if (ctx.format === 'json') {
		printJson(created);
		return;
	}
	process.stdout.write(`Created campaign: ${created.enc_id ?? ''}\n`);
}

export function registerEnterpriseCampaignsCreate(parent: Command): void {
	parent
		.command('create')
		.description('Create a recruitment campaign from a JSON file (use "-" to read stdin)')
		.requiredOption('--file <path>', 'path to a JSON campaign body, or "-" for stdin')
		.option('--idempotency-key <key>', 'reuse across retries to avoid duplicate creates (default: a fresh UUID)')
		.action(async (flags: CreateFlags, command: Command) => {
			const ctx = resolveContext(command);
			const globals = command.optsWithGlobals() as { apiKey?: string };
			const { key } = resolveApiKey(globals.apiKey);
			await runCampaignCreate(ctx, key, flags.file as string, flags.idempotencyKey ?? randomUUID());
		});
}
